/**
 * Compact mobile header for Employer Dashboard
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faEnvelope,
  faBuilding,
  faSignOutAlt,
  faUser,
  faBriefcase,
  faClipboardList,
  faCalendarCheck,
  faUserGraduate
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { buildApiUrl } from '../config/api';
import notificationApi from '../api/notificationApi';
import './EmployerDashboardMobileHeader.css';

const getApplicantInitials = (name) => {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return parts[0].substring(0, 2).toUpperCase();
};

const formatNotificationTime = (notification) => {
  const raw = notification.created_at || notification.createdAt || notification.timestamp;
  if (!raw) return null;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return null;

  const diffMs = Date.now() - date.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const parseNotificationContent = (notification) => {
  const data = notification.data || {};
  const message = (notification.message || '').trim();
  const title = (notification.title || '').trim();
  const type = String(notification.type || notification.notification_type || '').toLowerCase();

  const applicantName = data.applicant_name || data.candidate_name || '';
  const jobTitle = data.job_title || data.jobTitle || '';

  if (applicantName && jobTitle) {
    return {
      variant: 'application',
      name: applicantName,
      jobTitle,
      label: type.includes('internship') ? 'Internship application' : 'New application',
    };
  }

  const appliedMatch = message.match(/^(.+?)\s+has applied for\s+(.+)$/i);
  if (appliedMatch) {
    return {
      variant: 'application',
      name: appliedMatch[1].trim(),
      jobTitle: appliedMatch[2].trim(),
      label: 'New application',
    };
  }

  if (type.includes('message')) {
    return { variant: 'message', title: title || 'New message', body: message };
  }
  if (type.includes('interview')) {
    return { variant: 'interview', title: title || 'Interview update', body: message };
  }
  if (type.includes('status')) {
    return { variant: 'status', title: title || 'Application update', body: message };
  }
  if (type.includes('internship')) {
    return { variant: 'internship', title: title || 'Internship update', body: message };
  }

  return {
    variant: 'general',
    title: title || 'Notification',
    body: message || title || 'Notification',
  };
};

const getNotificationIcon = (variant, name) => {
  switch (variant) {
    case 'application':
      return <span className="em-notification-initials">{getApplicantInitials(name)}</span>;
    case 'message':
      return <FontAwesomeIcon icon={faEnvelope} />;
    case 'interview':
      return <FontAwesomeIcon icon={faCalendarCheck} />;
    case 'status':
      return <FontAwesomeIcon icon={faClipboardList} />;
    case 'internship':
      return <FontAwesomeIcon icon={faUserGraduate} />;
    default:
      return <FontAwesomeIcon icon={faBell} />;
  }
};

const EmployerDashboardMobileHeader = ({ onOpenMessages, isMessagesOpen = false }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [messageCount, setMessageCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [profilePhotoPath, setProfilePhotoPath] = useState(null);
  const [avatarError, setAvatarError] = useState(false);
  const userMenuRef = useRef(null);
  const notificationBtnRef = useRef(null);
  const messageBtnRef = useRef(null);
  const profileDropdownRef = useRef(null);
  const notificationDropdownRef = useRef(null);

  const closeAllMenus = useCallback(() => {
    setShowUserMenu(false);
    setShowNotifications(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inAvatar = userMenuRef.current?.contains(event.target);
      const inProfileMenu = profileDropdownRef.current?.contains(event.target);
      const inNotificationBtn = notificationBtnRef.current?.contains(event.target);
      const inNotificationMenu = notificationDropdownRef.current?.contains(event.target);

      if (!inAvatar && !inProfileMenu) {
        setShowUserMenu(false);
      }
      if (!inNotificationBtn && !inNotificationMenu) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchCounts = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const [notifRes, msgRes] = await Promise.all([
        axios.get(buildApiUrl('/api/notifications'), { headers: { Authorization: `Bearer ${token}` }, timeout: 5000 }),
        axios.get(buildApiUrl('/api/messages'), { headers: { Authorization: `Bearer ${token}` }, timeout: 5000 })
      ]);
      if (notifRes.data?.success) {
        const list = notifRes.data.notifications || [];
        setNotifications(list);
        setNotificationCount(list.filter((n) => !(n.read || n.is_read)).length);
      }
      if (msgRes.data?.success) {
        const list = msgRes.data.messages || [];
        setMessageCount(list.filter((m) => !m.read).length);
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    fetchCounts();
    const interval = setInterval(fetchCounts, 15000);
    return () => clearInterval(interval);
  }, [fetchCounts]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios
      .get(buildApiUrl('/api/recruiters/profile'), { headers: { Authorization: `Bearer ${token}` } })
      .then((response) => {
        const photo = response.data?.profilePhotoPath || response.data?.profilePhoto || response.data?.profileImage || response.data?.companyLogo;
        if (photo) {
          setProfilePhotoPath(photo);
          setAvatarError(false);
        }
      })
      .catch(() => {});
  }, []);

  const getProfilePhotoUrl = (path) => {
    if (!path) return null;
    if (typeof path === 'string' && (path.startsWith('http://') || path.startsWith('https://'))) return path;
    const normalized = path.replace(/\\/g, '/');
    if (normalized.startsWith('uploads/')) {
      return buildApiUrl(`/api/users/uploads/${normalized.replace('uploads/', '')}`);
    }
    return buildApiUrl(`/api/users/uploads/${normalized}`);
  };

  const getUserInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    return user?.email ? user.email.substring(0, 2).toUpperCase() : 'EM';
  };

  const getUserName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user?.email || 'Employer';
  };

  const handleLogout = () => {
    closeAllMenus();
    logout();
    navigate('/login');
  };

  const handleNotificationToggle = () => {
    setShowUserMenu(false);
    setShowNotifications((open) => !open);
  };

  const handleMessagesToggle = () => {
    setShowNotifications(false);
    setShowUserMenu(false);
    if (typeof onOpenMessages === 'function') {
      onOpenMessages();
    }
  };

  const handleMarkAllNotificationsRead = async () => {
    await notificationApi.markAllAsRead();
    fetchCounts();
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.read && notification._id) {
      await notificationApi.markAsRead(notification._id);
      fetchCounts();
    }
  };

  const photoUrl = user?.profileImage || (profilePhotoPath ? getProfilePhotoUrl(profilePhotoPath) : null);
  const showAvatarImage = Boolean(photoUrl) && !avatarError;

  return (
    <header className="em-mobile-header">
      <div
        className="em-mobile-header-brand"
        onClick={() => navigate('/employer-dashboard')}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/employer-dashboard'); }}
        role="button"
        tabIndex={0}
        aria-label="Go to dashboard home"
      >
        <div className="em-mobile-logo-icon" aria-hidden="true">
          <FontAwesomeIcon icon={faBuilding} />
        </div>
        <div className="em-mobile-logo-text">
          <span className="em-mobile-logo-name">Employer Hub</span>
          <span className="em-mobile-logo-subtitle">AksharJobs</span>
        </div>
      </div>

      <div className="em-mobile-header-actions">
        <button
          ref={notificationBtnRef}
          type="button"
          className="em-mobile-action-btn btn-custom-size"
          aria-label="Notifications"
          aria-expanded={showNotifications}
          onClick={handleNotificationToggle}
        >
          <FontAwesomeIcon icon={faBell} className="em-mobile-action-icon" />
          {notificationCount > 0 && <span className="em-mobile-badge">{notificationCount}</span>}
        </button>

        <button
          ref={messageBtnRef}
          type="button"
          className={`em-mobile-action-btn btn-custom-size${isMessagesOpen ? ' em-mobile-action-btn--active' : ''}`}
          aria-label="Messages"
          aria-expanded={isMessagesOpen}
          aria-pressed={isMessagesOpen}
          onClick={handleMessagesToggle}
        >
          <FontAwesomeIcon icon={faEnvelope} className="em-mobile-action-icon" />
          {messageCount > 0 && <span className="em-mobile-badge">{messageCount}</span>}
        </button>

        <button
          type="button"
          className="em-mobile-action-btn em-mobile-action-btn--avatar btn-custom-size"
          aria-label="Account menu"
          ref={userMenuRef}
          onClick={() => {
            setShowNotifications(false);
            setShowUserMenu((open) => !open);
          }}
        >
          {showAvatarImage ? (
            <img
              src={photoUrl}
              alt={getUserName()}
              className="em-mobile-action-avatar-img"
              onError={() => setAvatarError(true)}
            />
          ) : (
            <span className="em-mobile-avatar-initials">{getUserInitials()}</span>
          )}
        </button>

        {showNotifications && ReactDOM.createPortal(
          <div ref={notificationDropdownRef} className="em-mobile-dropdown-menu em-mobile-notifications-menu">
            <div className="em-mobile-dropdown-header">
              <h4>
                <FontAwesomeIcon icon={faBell} className="em-mobile-dropdown-header-icon" />
                Notifications
              </h4>
              {notificationCount > 0 && (
                <button type="button" className="em-mobile-dropdown-action btn-custom-size" onClick={handleMarkAllNotificationsRead}>
                  Mark all read
                </button>
              )}
            </div>
            <div className="em-mobile-dropdown-body">
              {notifications.length > 0 ? (
                notifications.slice(0, 12).map((notification, index) => {
                  const content = parseNotificationContent(notification);
                  const timeLabel = formatNotificationTime(notification);
                  const isUnread = !notification.read && notification.is_read !== true;

                  return (
                    <button
                      key={notification._id || index}
                      type="button"
                      className={`em-mobile-dropdown-item btn-custom-size${isUnread ? ' unread' : ''}`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="em-notification-item-row">
                        <div className={`em-notification-icon em-notification-icon--${content.variant}`}>
                          {getNotificationIcon(content.variant, content.name)}
                        </div>
                        <div className="em-notification-content">
                          {content.variant === 'application' ? (
                            <>
                              <div className="em-notification-title">
                                <span className="em-notification-name">{content.name}</span>
                                <span className="em-notification-label">{content.label}</span>
                              </div>
                              <div className="em-notification-meta">
                                <FontAwesomeIcon icon={faBriefcase} className="em-notification-meta-icon" />
                                <span>{content.jobTitle}</span>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="em-notification-title em-notification-title--single">
                                {content.title}
                              </div>
                              {content.body && (
                                <div className="em-notification-body">{content.body}</div>
                              )}
                            </>
                          )}
                          {timeLabel && (
                            <div className="em-notification-time">{timeLabel}</div>
                          )}
                        </div>
                        {isUnread && <span className="em-notification-unread-dot" aria-hidden="true" />}
                      </div>
                    </button>
                  );
                })
              ) : (
                <div className="em-mobile-dropdown-empty">
                  <FontAwesomeIcon icon={faBell} className="em-mobile-dropdown-empty-icon" />
                  <span>No new notifications</span>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}

        {showUserMenu && ReactDOM.createPortal(
          <div ref={profileDropdownRef} className="em-mobile-profile-menu">
            <div className="em-mobile-profile-menu-header">
              <div className="em-mobile-profile-menu-avatar">
                {showAvatarImage ? (
                  <img src={photoUrl} alt={getUserName()} onError={() => setAvatarError(true)} />
                ) : (
                  <span>{getUserInitials()}</span>
                )}
              </div>
              <div className="em-mobile-profile-menu-info">
                <div className="em-mobile-profile-menu-name">{getUserName()}</div>
                <div className="em-mobile-profile-menu-email">{user?.email}</div>
              </div>
            </div>

            <div className="em-mobile-profile-menu-body">
              <div
                className="em-mobile-profile-menu-item"
                role="button"
                tabIndex={0}
                onClick={() => { setShowUserMenu(false); navigate('/employer-my-profile'); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setShowUserMenu(false); navigate('/employer-my-profile'); } }}
              >
                <FontAwesomeIcon icon={faUser} />
                <span>My Profile</span>
              </div>

              <div className="em-mobile-profile-menu-divider" />

              <div
                className="em-mobile-profile-menu-item em-mobile-profile-menu-logout"
                role="button"
                tabIndex={0}
                onClick={handleLogout}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleLogout(); }}
              >
                <FontAwesomeIcon icon={faSignOutAlt} />
                <span>Logout</span>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </header>
  );
};

export default EmployerDashboardMobileHeader;
